/* eslint-disable react/jsx-no-bind -- one-line forwarding to non-memo'd children; component-anatomy.md lists this as a narrow exception */
import React from 'react'
import { shallowEqual, useSelector } from 'react-redux'
// eslint-disable-next-line no-restricted-imports -- legacy v5 router APIs (Redirect, Switch); migration to v6 is out of scope here
import { Redirect, Switch, useLocation } from 'react-router-dom'
import { CompatRoute } from 'react-router-dom-v5-compat'

import { useAuthValue } from 'auth/AuthContext'
import { GENERAL_VIEW_MODE, RECENTLY_ADDED_VIEW_MODE, RECENTLY_VIEWED_VIEW_MODE } from 'components/customers/accounts/settings'
import { useReviewAccess } from 'components/exceptions/hooks/useReviewAccess'
import CustomerDetails from 'containers/customer-details/CustomerDetails'
import {
  canSeeNotifications,
  canSeeBillingTab,
  canSeeCustomersTab,
  canSeeDashboardTab,
  canSeePricingTab,
  canSeeZones,
  canSeeCustomerLists,
  canCreateCustomerList,
  canSeeReportingTab,
  canSeeReportTab,
  canSeeRouteTab,
  canSeeSettingsTab,
} from 'data/permissions/permissionsSelectors'
import {
  selectIsInventoryFeatureEnabled,
  selectIsMessagingFeatureEnabled,
  selectIsNewTaxesEnabled,
} from 'data/tenant/tenantSettingsSelectors'
import {
  AccountGroupsDetailsPage,
  AccountGroupsPage,
  AccountsPage,
  BillingShell,
  ConfiguredServiceDetailsPage,
  DashboardPage,
  DisposalFeesPage,
  DisposalFeeDetailsPage,
  ErrorPage,
  ExportWorkOrderListPage,
  ExportWorkOrderPage,
  FollowUpPage,
  HelpPage,
  HomePage,
  InventoryContainersPage,
  InventoryContainersMovesPage,
  InventoryContainerAddEditPage,
  InventoryContainersImportPage,
  LateFeeDetailsPage,
  LateFeesPage,
  LocationsPage,
  LoginPage,
  NewRoutesPage,
  MasterSequencePage,
  PricingPage,
  RentalFeeDetailsPage,
  RentalFeesPage,
  ReportingShell,
  ReportsDetailPage,
  ReportsPage,
  SelectAccountPage,
  ServiceGroupsPage,
  SettingsPage,
  WODetailsPage,
  MessagingPage,
  ZonesPage,
  ZoneDetailPage,
  CustomerListDetailsPage,
  NewCustomerListPage,
  TaxesPage,
  TaxDetailPage,
  ExceptionsPage,
} from 'pages'
import ImpersonatePage from 'pages/impersonate/ImpersonatePage'
import NotificationCenterPage from 'pages/NotificationCenterPage'
import { handleFirebaseLogout } from 'providers/firebaseLogout'
import RedirectToValidCustomerDetailsUrlHelper, { singularCustomerRoutes } from 'router/RedirectToValidCustomerDetailsUrlHelper'
import routes, { IMPERSONATE_PATH } from 'router/routes'
import { selectHomePage } from 'slices/home/homeSlice'
import { selectInventoryFavoriteUrl } from 'slices/Inventory/inventoryDrawerSlice'
import { selectPricingFavoriteUrl } from 'slices/pricing/pricingDrawerSlice'
import { selectReportingFavoriteUrl } from 'slices/reporting/reportingDrawerSlice'

import ProtectedRoute from './ProtectedRoute'

const MainRoute = () => {
  const location = useLocation()
  const homePage = useSelector(selectHomePage, shallowEqual)
  const pricingFavoriteUrl = useSelector(selectPricingFavoriteUrl, shallowEqual)
  const inventoryFavoriteUrl = useSelector(selectInventoryFavoriteUrl, shallowEqual)
  const reportingFavoriteUrl = useSelector(selectReportingFavoriteUrl, shallowEqual)
  const { currentUser } = useAuthValue() || {}
  const isInventoryFeatureEnabled = useSelector(selectIsInventoryFeatureEnabled, shallowEqual)
  const { isReviewEnabled, defaultSlug } = useReviewAccess()
  const isMessagingEnabled = useSelector(selectIsMessagingFeatureEnabled, shallowEqual)
  const isNewTaxesEnabled = useSelector(selectIsNewTaxesEnabled, shallowEqual)

  return (
    <Switch>
      <CompatRoute exact component={ImpersonatePage} path={IMPERSONATE_PATH} />
      <CompatRoute component={LoginPage} path={routes.app.login} />
      <ProtectedRoute exact component={() => handleFirebaseLogout()} path={routes.app.logout} />
      <ProtectedRoute exact component={SelectAccountPage} path={routes.app.selectAccount} />
      <ProtectedRoute exact component={HomePage} path={routes.app.home} />
      {canSeeDashboardTab() && <ProtectedRoute exact component={DashboardPage} path={routes.app.dashboard} />}
      {/* Redirect old url to new url */}
      {canSeeCustomersTab() && <Redirect exact from={routes.app.customers} to={homePage} />}
      {canSeeCustomersTab() && (
        <ProtectedRoute component={() => <AccountsPage viewMode={GENERAL_VIEW_MODE} />} path={routes.app.accounts} />
      )}
      {canSeeCustomersTab() && (
        <ProtectedRoute component={() => <AccountsPage viewMode={RECENTLY_ADDED_VIEW_MODE} />} path={routes.app.recentlyAdded} />
      )}
      {canSeeCustomersTab() && (
        <ProtectedRoute component={() => <AccountsPage viewMode={RECENTLY_VIEWED_VIEW_MODE} />} path={routes.app.recentlyViewed} />
      )}
      {/* Redirect singular url to standard url */}
      {canSeeCustomersTab() && (
        <ProtectedRoute exact component={RedirectToValidCustomerDetailsUrlHelper} path={singularCustomerRoutes.customers} />
      )}
      {canSeeCustomersTab() && (
        <ProtectedRoute exact component={RedirectToValidCustomerDetailsUrlHelper} path={singularCustomerRoutes.customerDetails} />
      )}
      {canSeeCustomersTab() && (
        <ProtectedRoute exact component={RedirectToValidCustomerDetailsUrlHelper} path={singularCustomerRoutes.accounts} />
      )}
      {canSeeCustomersTab() && (
        <ProtectedRoute component={RedirectToValidCustomerDetailsUrlHelper} path={singularCustomerRoutes.customerDetailsVariant} />
      )}
      {canSeeCustomersTab() && <ProtectedRoute exact component={LocationsPage} path={routes.app.locations} />}
      {canSeeCustomersTab() && <Redirect exact from={routes.app.suspensionGroups} to={routes.app.suspendedAccounts} />}
      {canSeeCustomersTab() && <ProtectedRoute exact component={AccountGroupsDetailsPage} path={`${routes.app.suspensionGroups}/:id`} />}
      {canSeeCustomersTab() && <ProtectedRoute exact component={AccountGroupsPage} path={routes.app.accountGroups} />}
      {canSeeCustomersTab() && canCreateCustomerList() && (
        <ProtectedRoute exact component={NewCustomerListPage} path={routes.app.newCustomerList} />
      )}
      {canSeeCustomersTab() && canSeeCustomerLists() && (
        <ProtectedRoute exact component={CustomerListDetailsPage} path={routes.app.customerListDetails} />
      )}
      {canSeeCustomersTab() && <ProtectedRoute exact component={AccountGroupsDetailsPage} path={`${routes.app.accountGroups}/:id`} />}
      {canSeeCustomersTab() && <ProtectedRoute exact component={CustomerDetails} path={routes.app.customerDetails} />}
      {canSeeCustomersTab() && <ProtectedRoute exact component={WODetailsPage} path={routes.app.workOrderDetails} />}
      <ProtectedRoute component={ExportWorkOrderListPage} path={routes.app.exportRouteWorkOrders} />
      <ProtectedRoute component={ExportWorkOrderPage} path={routes.app.exportRouteWorkOrder} />
      {canSeeRouteTab() && <ProtectedRoute exact component={NewRoutesPage} path={routes.app.routes} />}
      {canSeeRouteTab() && <ProtectedRoute exact component={MasterSequencePage} path={routes.app.masterSequence} />}
      {canSeeBillingTab() && (
        // Must precede the billing shell route below, whose non-exact prefix match would swallow this redirect.
        <Redirect exact from={routes.app.workOrdersReviewV2} to={{ pathname: routes.app.workOrdersReview, search: location?.search }} />
      )}
      {/* One route for every billing destination, so the shell stays mounted across navigation */}
      {canSeeBillingTab() && <ProtectedRoute exact component={BillingShell} path={`${routes.app.billing}/*`} />}
      <ProtectedRoute exact component={FollowUpPage} path={routes.app.followUp} />
      {canSeeReportTab() && <ProtectedRoute exact component={ReportsPage} path={routes.app.reports} />}
      {canSeeReportTab() && <ProtectedRoute exact component={ReportsDetailPage} path={routes.app.reportDetails} />}
      {canSeeReportingTab() && <Redirect exact from={routes.app.reporting} to={reportingFavoriteUrl} />}
      {/* One route for every reporting destination, so the shell stays mounted across navigation */}
      {canSeeReportingTab() && <ProtectedRoute exact component={ReportingShell} path={routes.app.reportingDetails} />}
      {canSeePricingTab() && <Redirect exact from={routes.app.pricing} to={pricingFavoriteUrl} />}
      {canSeePricingTab() && <ProtectedRoute exact component={PricingPage} path={routes.app.pricingServices} />}
      {canSeePricingTab() && <ProtectedRoute exact component={PricingPage} path={routes.app.pricingFees} />}
      {canSeePricingTab() && <ProtectedRoute exact component={PricingPage} path={routes.app.pricingTaxes} />}
      {canSeePricingTab() && <ProtectedRoute exact component={ServiceGroupsPage} path={routes.app.pricingServiceGroups} />}
      {canSeePricingTab() && <ProtectedRoute exact component={LateFeesPage} path={routes.app.pricingLateFees} />}
      {canSeePricingTab() && <ProtectedRoute exact component={LateFeeDetailsPage} path={routes.app.pricingLateFeeDetails} />}
      {canSeePricingTab() && <ProtectedRoute exact component={RentalFeesPage} path={routes.app.pricingRentalFees} />}
      {canSeePricingTab() && <ProtectedRoute exact component={RentalFeeDetailsPage} path={routes.app.pricingRentalFeeDetails} />}
      {canSeePricingTab() && <ProtectedRoute exact component={DisposalFeesPage} path={routes.app.pricingDisposalFees} />}
      {canSeePricingTab() && <ProtectedRoute exact component={DisposalFeeDetailsPage} path={routes.app.pricingDisposalFeeDetails} />}
      <Redirect exact from={routes.app.pricingTaxZoneDetail} to={routes.app.pricingZoneDetail} />
      <Redirect exact from={routes.app.pricingTaxZones} to={routes.app.pricingZones} />
      {canSeeZones() && isNewTaxesEnabled && <ProtectedRoute exact component={ZonesPage} path={routes.app.pricingZones} />}
      {canSeeZones() && isNewTaxesEnabled && <ProtectedRoute exact component={ZoneDetailPage} path={routes.app.pricingZoneDetail} />}
      {canSeePricingTab() && <ProtectedRoute exact component={TaxesPage} path={routes.app.pricingTaxesNew} />}
      {canSeePricingTab() && <ProtectedRoute exact component={TaxDetailPage} path={routes.app.pricingTaxDetail} />}
      {canSeeCustomersTab() && <ProtectedRoute exact component={ConfiguredServiceDetailsPage} path={routes.app.configuredServiceDetails} />}
      {canSeeSettingsTab() && <ProtectedRoute exact component={SettingsPage} path={routes.app.settingDetails} />}
      <Redirect exact from={routes.app.inventory} to={inventoryFavoriteUrl} />
      {isInventoryFeatureEnabled && <ProtectedRoute exact component={InventoryContainersPage} path={routes.app.inventoryContainers} />}
      {isInventoryFeatureEnabled && (
        <ProtectedRoute exact component={InventoryContainersMovesPage} path={routes.app.inventoryContainersMoves} />
      )}
      {isInventoryFeatureEnabled && <ProtectedRoute exact component={InventoryContainerAddEditPage} path={routes.app.addContainer} />}
      {isInventoryFeatureEnabled && (
        <ProtectedRoute exact component={InventoryContainerAddEditPage} path={`${routes.app.editContainer}/:id`} />
      )}
      {isInventoryFeatureEnabled && (
        <ProtectedRoute exact component={InventoryContainersPage} path={`${routes.app.inventoryContainerStatus}/:id`} />
      )}
      {isInventoryFeatureEnabled && <ProtectedRoute exact component={InventoryContainersPage} path={routes.app.inventoryContainerYard} />}
      {isInventoryFeatureEnabled && (
        <ProtectedRoute exact component={InventoryContainersPage} path={`${routes.app.inventoryContainerYard}/:id`} />
      )}
      {isInventoryFeatureEnabled && <ProtectedRoute exact component={InventoryContainersImportPage} path={routes.app.importContainers} />}
      <ProtectedRoute exact component={HelpPage} path={routes.app.help} />
      {isMessagingEnabled && (
        <Redirect
          exact
          from={`${routes.app.messaging}/broadcast`}
          to={{ pathname: routes.app.messagingNewMessage, search: location?.search }}
        />
      )}
      {isMessagingEnabled && <ProtectedRoute exact component={MessagingPage} path={routes.app.messagingDetail} />}
      {isMessagingEnabled && <ProtectedRoute exact component={MessagingPage} path={routes.app.messaging} />}
      {canSeeNotifications() && <ProtectedRoute exact component={NotificationCenterPage} path={routes.app.notifications} />}
      {isReviewEnabled && <Redirect exact from={routes.app.visionExceptions} to={`${routes.app.visionExceptions}/${defaultSlug}`} />}
      {isReviewEnabled && <ProtectedRoute exact component={ExceptionsPage} path={routes.app.visionExceptionsDetails} />}
      <ProtectedRoute exact component={ErrorPage} path={routes.app.error} />
      <Redirect to={{ pathname: currentUser ? homePage : routes.app.login, search: location?.search }} />
    </Switch>
  )
}

export default MainRoute
